export class Sort {
    /**
     * ソート
     *
     * @constructor
     * @param {HTMLElement} root - ルートとなる要素
     * @param {Object} options - 設定の変更をする際のオブジェクト
     */
    constructor(root, options) {
        const config = {
            className: {
                container: 'js-sort__container',
                ctrl: 'js-sort__ctrl',
                date: 'p-layout-product__date',
                price: 'c-text-price__price'
            }
        };

        // ルートとなる要素が無い場合は何もしない
        if (!root) {
            return;
        }

        this.config = Object.assign(config, options);
        this.root = root;
        this.ctrl = this.root.querySelector(`.${this.config.className.ctrl}`);
        this.options = Array.from(this.ctrl.children);
        this.sortOptions = this.options.map((element) => element.value);
        this.container = this.root.querySelector(`.${this.config.className.container}`);
        this.sortTargets = this.container.children;
        this.prices = this.sortOptions.includes('cheap') || this.sortOptions.includes('expensive') ? this.getPrices() : [];
        this.releaseDates = this.sortOptions.includes('releaseDate') || this.sortOptions.includes('releaseDateOld') ? this.getReleaseDates() : [];
        this.sortedElements = this.sortElements();
        this.currentSortOption = this.ctrl.value;

        this.swap();
        this.addEvent();
    }

    /**
     * getPrices - 各アイテムの価格を取得
     * @return {Array}
     */
    getPrices() {
        const prices = [];

        for (let i = 0; i < this.sortTargets.length; i++) {
            const targetPrice = this.sortTargets[i].querySelectorAll(`.${this.config.className.price}`);
            const targetPrices = [];

            // 価格表示が1つの場合
            if (targetPrice.length <= 1) {
                prices.push({
                    index: i,
                    value: [!targetPrice.length ? 0 : parseInt(targetPrice[0].textContent.replace(/[^\d]/g, ''), 10)]
                });

                continue;
            }

            // 価格を配列に格納
            for (const price of targetPrice) {
                targetPrices.push(parseInt(price.textContent.replace(/[^\d]/g, ''), 10));
            }

            // 価格表示が複数ある場合、[最高価格, 最低価格]の形式で追加
            prices.push({
                index: i,
                value: [Math.max(...targetPrices), Math.min(...targetPrices)]
            });
        }

        return prices;
    }

    /**
     * getReleaseDates - 発売日を取得
     * @return {Array}
     */
    getReleaseDates() {
        const dates = [];

        // 各要素の発売日を取得・配列に格納
        for (let i = 0; i < this.sortTargets.length; i++) {
            const targetDate = this.sortTargets[i].querySelector(`.${this.config.className.date}`);

            // data-date属性が付与されている場合はdata-date属性の値を優先する
            if (targetDate && targetDate.dataset.date) {
                dates.push({
                    index: i,
                    value: parseInt(targetDate.dataset.date.replace(/[^\d]/g, ''), 10)
                });
            } else {
                dates.push({
                    index: i,
                    value: !targetDate ? 0 : parseInt(targetDate.textContent.replace(/[^\d]/g, ''), 10)
                });
            }
        }

        return dates;
    }

    /**
     * addEvent - イベントバインド
     */
    addEvent() {
        this.ctrl.addEventListener('change', (e) => {
            const targetValue = e.target.value;

            // 選択中のものと同じ選択肢を選んだ場合は何もしない
            if (this.currentSortOption === targetValue) {
                return;
            }

            this.currentSortOption = e.target.value;
            this.swap();
        });
    }

    /**
     * sortElements - ソートされた状態の要素の配列を種類別に取得
     * @return {Object}
     */
    sortElements() {
        const elements = {};

        // optionの数だけ実行
        for (const sortOption of this.sortOptions) {
            elements[sortOption] = this.sort()[sortOption]();
        }

        return elements;
    }

    /**
     * sort - ソートを実行
     * @return {Array}
     */
    sort() {
        const that = this;

        return {
            // おすすめ順（デフォルトの順序。今日→未来→過去）
            recommended() {
                return [...that.sortTargets];
            },
            // 発売日の新しい順（未来→過去）
            releaseDate() {
                return that.releaseDates.sort((a, b) => b.value - a.value).map((item) => that.sortTargets[item.index]);
            },
            // 発売日の古い順（過去→未来）
            releaseDateOld() {
                return this.releaseDate().reverse();
            },
            // 価格が安い順
            cheap() {
                return that.prices.sort((a, b) => a.value.slice(-1)[0] - b.value.slice(-1)[0]).map((item) => that.sortTargets[item.index]);
            },
            // 価格が高い順
            expensive() {
                return that.prices.sort((a, b) => b.value[0] - a.value[0]).map((item) => that.sortTargets[item.index]);
            },
            // お気に入り追加した新しい順（デフォルトの順序）
            addNewest() {
                return [...that.sortTargets];
            },
            // お気に入り追加した古い順（デフォルトの順序の逆順）
            addOldest() {
                return [...that.sortTargets].reverse();
            }
        };
    }

    /**
     * swap - 要素を入れ替え
     * @return {Void}
     */
    swap() {
        const fragments = document.createDocumentFragment();

        for (const element of this.sortedElements[this.currentSortOption]) {
            fragments.appendChild(element);
        }

        Array.from(this.container.children).map((element) => this.container.removeChild(element));
        this.container.appendChild(fragments);
    }
}

/**
 * デフォルト実行処理
 * @return {Void}
 */
export default () => {
    // ソート
    document.querySelectorAll('.js-sort').forEach((element) => new Sort(element));
};
